/**
 * useProspectDocuments Hook
 * Manages prospect document state with signed URL caching
 * Implements 15-minute expiry buffer strategy (matches mobile and video implementation)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import type { ProspectDocument, CachedSignedUrl } from '../types/prospect-media';

const EXPIRY_BUFFER_MINUTES = 15;

export function useProspectDocuments(prospectId: string) {
  const [documents, setDocuments] = useState<ProspectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [documentUrlCache, setDocumentUrlCache] = useState<Map<string, CachedSignedUrl>>(new Map());
  const [refreshing, setRefreshing] = useState(false);

  // Fetch documents from API
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedDocuments = await prospectMediaService.getDocuments(prospectId);

      // Sort by date (newest first)
      const sortedDocuments = fetchedDocuments.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setDocuments(sortedDocuments);
      return sortedDocuments;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[ProspectDocuments] Failed to fetch documents', {
        error: err,
        prospectId,
      });
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  // Update cache when documents change
  useEffect(() => {
    const newDocumentCache = new Map<string, CachedSignedUrl>();

    documents.forEach((document) => {
      newDocumentCache.set(document.id, {
        signedUrl: document.gcsUrl,
        expiresAt: new Date(document.urlExpiresAt),
      });
    });

    setDocumentUrlCache(newDocumentCache);
  }, [documents]);

  // Check if URLs need refresh (15-minute buffer)
  const needsRefresh = useMemo(() => {
    if (documentUrlCache.size === 0) return false;

    const now = Date.now();
    const bufferMs = EXPIRY_BUFFER_MINUTES * 60 * 1000;

    for (const cached of documentUrlCache.values()) {
      const timeUntilExpiry = cached.expiresAt.getTime() - now;
      if (timeUntilExpiry < bufferMs) {
        return true;
      }
    }

    return false;
  }, [documentUrlCache]);

  // Get document URL with expiry check
  const getDocumentUrl = useCallback(
    async (documentId: string): Promise<string> => {
      const cached = documentUrlCache.get(documentId);

      // If expired or not cached, refresh
      if (!cached || cached.expiresAt.getTime() < Date.now()) {
        setRefreshing(true);
        try {
          const fetchedDocuments = await fetchDocuments();
          const document = fetchedDocuments.find((d) => d.id === documentId);
          if (!document) {
            console.error('[ProspectDocuments] Document not found after refresh', {
              documentId,
              prospectId,
            });
            throw new Error('Document not found. It may have been deleted.');
          }
          return document.gcsUrl;
        } catch (error) {
          console.error('[ProspectDocuments] Failed to refresh document URLs', {
            error,
            documentId,
            prospectId,
          });
          // Re-throw to let caller handle
          throw error;
        } finally {
          setRefreshing(false);
        }
      }

      return cached.signedUrl;
    },
    [documentUrlCache, fetchDocuments, prospectId]
  );

  // Delete document with optimistic update
  const deleteDocument = useCallback(
    async (documentId: string) => {
      // Optimistic update
      const previousDocuments = documents;
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));

      try {
        await prospectMediaService.deleteDocument(prospectId, documentId);
      } catch (err) {
        console.error('[ProspectDocuments] Failed to delete document', {
          error: err,
          prospectId,
          documentId,
        });
        // Revert on error
        setDocuments(previousDocuments);
        throw err;
      }
    },
    [prospectId, documents]
  );

  // Separate documents into regular and handwritten notes
  const regularDocuments = useMemo(() => {
    return documents.filter((doc) => !doc.isHandwrittenNote);
  }, [documents]);

  const handwrittenNotes = useMemo(() => {
    return documents.filter((doc) => doc.isHandwrittenNote);
  }, [documents]);

  const handwrittenNotesCount = useMemo(() => {
    return handwrittenNotes.length;
  }, [handwrittenNotes]);

  // Initial fetch
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refresh: fetchDocuments,
    deleteDocument,
    getDocumentUrl,
    needsRefresh,
    refreshing,
    regularDocuments,
    handwrittenNotes,
    handwrittenNotesCount,
  };
}
